import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import {
  hasAnyRole,
  ROLE_USEDCAR,
  ROLE_BRUKTBILFINANS,
  ROLE_LEADS,
  ROLE_OPPSLAG_BASIC,
} from '../utils/roles'
import {
  LayoutDashboard,
  BarChart3,
  FileText,
  // Building2, // Commented out with Firmabilsalg
  Tags,
  // MapPin, // Commented out with Geografi
  Users,
  User,
  Calendar,
  BookOpen,
  DollarSign,
  Banknote,
  Car,
  // Truck, // Commented out with Varebilanalyse
  TrendingUp,
  MessageSquare,
  Info,
  Bell,
  PieChart,
  Target,
  SearchCheck,
  Wrench,
  Truck,
} from 'lucide-react'

export default function Sidebar() {
  const { t } = useTranslation('nav')
  const { user } = useAuth()
  const canSeeBruktbil = hasAnyRole(user, [ROLE_USEDCAR], false)
  const canSeeBruktbilfinans = hasAnyRole(user, [ROLE_BRUKTBILFINANS])
  const canSeeLeads = hasAnyRole(user, [ROLE_LEADS])
  // Only basic opens the Oppslag page — premium alone is an API entitlement
  // with no visual access; the tabs inside are gated separately.
  const canSeeOppslag = hasAnyRole(user, [ROLE_OPPSLAG_BASIC])

  const navItems: { path: string; icon: React.ComponentType<{ className?: string }>; label: string; badge?: string }[] = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('sidebar.dashboard') },
    { path: '/total-statistics', icon: BarChart3, label: t('sidebar.totalStatistics') },
    { path: '/brand-comparison', icon: TrendingUp, label: t('sidebar.periods') },
    { path: '/age-analytics', icon: Calendar, label: t('sidebar.age') },
    { path: '/teknisk', icon: Wrench, label: t('sidebar.technical') },
    { path: '/segment-analytics', icon: PieChart, label: t('sidebar.segments') },
    { path: '/leasing', icon: FileText, label: t('sidebar.leasing') },
    { path: '/loans', icon: DollarSign, label: t('sidebar.loans') },
    { path: '/organizations', icon: Users, label: t('sidebar.business') },
    { path: '/andre-kjoretoy', icon: Truck, label: t('sidebar.otherVehicles'), badge: t('sidebar.beta') },
    { path: '/kampanjer', icon: Tags, label: t('sidebar.campaigns') },
  ]

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center space-x-3 px-4 py-3 rounded-lg transition ${
      isActive
        ? 'bg-primary-500 dark:bg-primary-600 text-white'
        : 'text-primary-100 hover:bg-primary-500/50 dark:hover:bg-primary-700'
    }`

  return (
    <aside className="w-64 bg-linear-to-b from-primary-600 to-primary-700 dark:from-primary-800 dark:to-primary-900 text-white min-h-screen flex flex-col transition-colors duration-200">
      {/* Logo Area */}
      <div className="p-6 border-b border-primary-500 dark:border-primary-600">
        <div className="flex items-center space-x-2">
          <BarChart3 className="w-6 h-6" />
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-primary-500 hover:scrollbar-thumb-primary-400 scrollbar-track-transparent">
        {navItems.map((item) => (
          <NavLink key={item.path} to={item.path} className={linkClass}>
            <item.icon className="w-5 h-5" />
            <span className="font-medium whitespace-nowrap">{item.label}</span>
            {item.badge && (
              <span className="ml-auto shrink-0 text-[8px] leading-none font-semibold uppercase bg-amber-400 text-amber-900 px-1 py-0.5 rounded-sm">
                {item.badge}
              </span>
            )}
          </NavLink>
        ))}

        {/* Bruktbilfinansiering — gated by bruktbilfinans_selectedbank_user role */}
        {canSeeBruktbilfinans && (
        <div className="pt-4 mt-4 border-t border-primary-500 dark:border-primary-600">
          <NavLink to="/bruktbil-finansiering" className={linkClass}>
            <Banknote className="w-5 h-5" />
            <span className="font-medium">{t('sidebar.usedCarFinancing')}</span>
          </NavLink>
        </div>
        )}

        {/* Bruktbil used-car analytics — gated by usedcartransfer_basic role */}
        {canSeeBruktbil && (
        <div data-testid="bruktbil-nav" className="pt-4 mt-4 border-t border-primary-500 dark:border-primary-600">
          <NavLink to="/bruktbil" className={linkClass}>
            <Car className="w-5 h-5" />
            <span className="font-medium">{t('sidebar.usedCars')}</span>
          </NavLink>
        </div>
        )}

        {/* Leads — gated by leads_advanced_user role */}
        {canSeeLeads && (
        <div data-testid="leads-nav" className="pt-4 mt-4 border-t border-primary-500 dark:border-primary-600">
          <NavLink to="/leads" className={linkClass}>
            <Target className="w-5 h-5" />
            <span className="font-medium">{t('sidebar.leads')}</span>
          </NavLink>
        </div>
        )}

        {/* Oppslag — gated by either oppslag role */}
        {canSeeOppslag && (
        <div data-testid="oppslag-nav" className="pt-4 mt-4 border-t border-primary-500 dark:border-primary-600">
          <NavLink to="/oppslag" className={linkClass}>
            <SearchCheck className="w-5 h-5" />
            <span className="font-medium">{t('sidebar.lookup')}</span>
          </NavLink>
        </div>
        )}

        {/* User Profile Links (separated) */}
        <div className="pt-4 mt-4 border-t border-primary-500 dark:border-primary-600">
          <NavLink to="/profile" className={linkClass}>
            <User className="w-5 h-5" />
            <span className="font-medium">{t('sidebar.myProfile')}</span>
          </NavLink>
          <NavLink to="/hendelsessenter" className={linkClass}>
            <Bell className="w-5 h-5" />
            <span className="font-medium">{t('sidebar.eventCentre')}</span>
          </NavLink>
          <NavLink to="/announcements" className={linkClass}>
            <BookOpen className="w-5 h-5" />
            <span className="font-medium">{t('sidebar.instructions')}</span>
          </NavLink>
          <NavLink to="/feedback" className={linkClass}>
            <MessageSquare className="w-5 h-5" />
            <span className="font-medium">{t('sidebar.feedback')}</span>
          </NavLink>
          <NavLink to="/about" className={linkClass}>
            <Info className="w-5 h-5" />
            <span className="font-medium">{t('sidebar.about')}</span>
          </NavLink>
        </div>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-primary-500 dark:border-primary-600">
        <p className="text-xs text-primary-200">
          © 2026 bilpriser.no
        </p>
      </div>
    </aside>
  )
}
